import urlFunctionsFactory from '@boc/framework/urls/factory';

import config from '~/config';

import { channels } from '#/channels';
import { supportedLocales } from '#/locales';

export const BASE_URL = `${config.PUBLIC_HTTP_SCHEME || 'https'}://${config.PUBLIC_SITE_DOMAIN}`;

const {
  withLocaleChannel,
  withLocaleChannelWithVars,
  parseParams,
  schLocaleChannel,
  absoluteify,
  canonicalize,
  imgUrl,
} = urlFunctionsFactory({ channels, baseURL: BASE_URL, locales: supportedLocales });

const urls = {
  healthcheck: '/healthcheck/',

  home: withLocaleChannel((locale, channel) => `/${channel.urlSlug}/${locale}/`),
  newsletter: withLocaleChannel((locale, channel) => `/${channel.urlSlug}/${locale}/newsletter/`),
  boc: {
    boardGames: withLocaleChannel(
      (_, channel) => `https://in.boredgamecompany.com/?utm_source=${channel.slug}`
    ),
    pokemon: withLocaleChannel(
      (_, channel) =>
        `https://tcg.boredgamecompany.com/in/en/products/category/tcg/brand-pokemon?utm_source=${channel.slug}`
    ),
    kpop: withLocaleChannel(
      (_, channel) =>
        `https://in.boredgamecompany.com/categories/card-sleeves/?utm_source=${channel.slug}`
    ),
    explodingKittens: withLocaleChannel(
      (locale, channel) =>
        // boredgamecompany.com only has in/ae channels; send everyone else to in
        `https://boredgamecompany.com/${channel.urlSlug === 'ae' ? 'ae' : 'in'}/${locale}/product/exploding-kittens-original-edition?utm_source=${channel.slug}`
    ),
  },
  general: {
    contact: withLocaleChannel((locale, channel) => `/${channel.urlSlug}/${locale}/contact/`),
    about: withLocaleChannel((locale, channel) => `/${channel.urlSlug}/${locale}/about-us/`),
    trackOrder: withLocaleChannel((locale, channel) => `/${channel.urlSlug}/${locale}/track/`),
    missingPieces: withLocaleChannel(
      (locale, channel) => `/${channel.urlSlug}/${locale}/missing-pieces/`
    ),
    kickstarter: withLocaleChannel(
      (locale, channel) =>
        `https://www.kickstarter.com/projects/azizmb/matchbox-puzzles-99-pc-mini-indian-puzzles-by-jigsawnation?ref=6kmx8v`
    ),
  },
  legal: {
    terms: withLocaleChannel((locale, channel) => `/${channel.urlSlug}/${locale}/legal/terms/`),
    returns: withLocaleChannel((locale, channel) => `/${channel.urlSlug}/${locale}/legal/returns/`),
    privacy: withLocaleChannel((locale, channel) => `/${channel.urlSlug}/${locale}/legal/privacy/`),
    shipping: withLocaleChannel(
      (locale, channel) => `/${channel.urlSlug}/${locale}/legal/shipping/`
    ),
  },
  checkout: {
    cart: withLocaleChannel((locale, channel) => `/${channel.urlSlug}/${locale}/checkout/cart/`),
    cartById: withLocaleChannelWithVars<{ checkoutId: string }>(
      (locale, channel, { checkoutId }) =>
        `/${channel.urlSlug}/${locale}/checkout/cart/${checkoutId}/`
    ),

    address: withLocaleChannel(
      (locale, channel) => `/${channel.urlSlug}/${locale}/checkout/address/`
    ),
    shipping: withLocaleChannel(
      (locale, channel) => `/${channel.urlSlug}/${locale}/checkout/shipping/`
    ),
    addressValidationRules: withLocaleChannel(
      (locale, channel) => `/${channel.urlSlug}/${locale}/checkout/address-validation-rules/`
    ),
  },
  accounts: {
    login: withLocaleChannel((locale, channel) => `/${channel.urlSlug}/${locale}/accounts/login/`),
    register: withLocaleChannel(
      (locale, channel) => `/${channel.urlSlug}/${locale}/accounts/register/`
    ),
  },
  orders: {
    track: withLocaleChannel((locale, channel) => `/${channel.urlSlug}/${locale}/order/track/`),
    summary: withLocaleChannelWithVars<{ orderId: string }>(
      (locale, channel, { orderId }) => `/${channel.urlSlug}/${locale}/order/${orderId}/summary/`
    ),
    payment: {
      index: withLocaleChannelWithVars<{ orderId: string }>(
        (locale, channel, { orderId }) =>
          `/${channel.urlSlug}/${locale}/order/${orderId}/payment/`
      ),
      razorpay: withLocaleChannelWithVars<{ orderId: string }>(
        (locale, channel, { orderId }) =>
          `/${channel.urlSlug}/${locale}/order/${orderId}/payment/razorpay/`
      ),
      ziina: withLocaleChannelWithVars<{ orderId: string }>(
        (locale, channel, { orderId }) =>
          `/${channel.urlSlug}/${locale}/order/${orderId}/payment/ziina/`
      ),
      ziinaComplete: withLocaleChannelWithVars<{ orderId: string }>(
        (locale, channel, { orderId }) =>
          `/${channel.urlSlug}/${locale}/order/${orderId}/payment/ziina/complete/`
      ),
      paymennt: withLocaleChannelWithVars<{ orderId: string }>(
        (locale, channel, { orderId }) =>
          `/${channel.urlSlug}/${locale}/order/${orderId}/payment/paymennt/`
      ),
      paymenntComplete: withLocaleChannelWithVars<{ orderId: string }>(
        (locale, channel, { orderId }) =>
          `/${channel.urlSlug}/${locale}/order/${orderId}/payment/paymennt/complete/`
      ),
    },
  },
  checkoutPayment: {
    index: withLocaleChannelWithVars<{ checkoutId: string }>(
      (locale, channel, { checkoutId }) =>
        `/${channel.urlSlug}/${locale}/checkout/${checkoutId}/payment/`
    ),
    razorpay: withLocaleChannelWithVars<{ checkoutId: string }>(
      (locale, channel, { checkoutId }) =>
        `/${channel.urlSlug}/${locale}/checkout/${checkoutId}/payment/razorpay/`
    ),
    ziina: withLocaleChannelWithVars<{ checkoutId: string }>(
      (locale, channel, { checkoutId }) =>
        `/${channel.urlSlug}/${locale}/checkout/${checkoutId}/payment/ziina/`
    ),
    ziinaComplete: withLocaleChannelWithVars<{ checkoutId: string }>(
      (locale, channel, { checkoutId }) =>
        `/${channel.urlSlug}/${locale}/checkout/${checkoutId}/payment/ziina/complete/`
    ),
    paymennt: withLocaleChannelWithVars<{ checkoutId: string }>(
      (locale, channel, { checkoutId }) =>
        `/${channel.urlSlug}/${locale}/checkout/${checkoutId}/payment/paymennt/`
    ),
    paymenntComplete: withLocaleChannelWithVars<{ checkoutId: string }>(
      (locale, channel, { checkoutId }) =>
        `/${channel.urlSlug}/${locale}/checkout/${checkoutId}/payment/paymennt/complete/`
    ),
  },
  customPuzzle: {
    landing: withLocaleChannelWithVars<{ variant?: string }>((locale, channel, { variant }) =>
      variant
        ? `/${channel.urlSlug}/${locale}/custom-puzzle/${variant}/`
        : `/${channel.urlSlug}/${locale}/custom-puzzle/`
    ),
    step1: withLocaleChannel(
      (locale, channel) => `/${channel.urlSlug}/${locale}/custom-puzzle/steps/1/`
    ),
    step2: withLocaleChannel(
      (locale, channel) => `/${channel.urlSlug}/${locale}/custom-puzzle/steps/2/`
    ),
    cart: withLocaleChannelWithVars<{ checkoutId: string }>(
      (locale, channel, { checkoutId }) =>
        `/${channel.urlSlug}/${locale}/custom-puzzle/steps/cart/${checkoutId}/`
    ),
    address: withLocaleChannelWithVars<{ checkoutId: string }>(
      (locale, channel, { checkoutId }) =>
        `/${channel.urlSlug}/${locale}/custom-puzzle/steps/address/${checkoutId}/`
    ),
    payment: withLocaleChannelWithVars<{ orderId: string }>(
      (locale, channel, { orderId }) =>
        `/${channel.urlSlug}/${locale}/custom-puzzle/steps/payment/${orderId}/`
    ),
    orderSummary: withLocaleChannelWithVars<{ orderId: string }>(
      (locale, channel, { orderId }) =>
        `/${channel.urlSlug}/${locale}/custom-puzzle/steps/order/${orderId}/`
    ),
  },
  customMapPuzzle: {
    landing: withLocaleChannelWithVars<{ variant?: string }>((locale, channel, { variant }) =>
      variant
        ? `/${channel.urlSlug}/${locale}/custom-map-puzzle/${variant}/`
        : `/${channel.urlSlug}/${locale}/custom-map-puzzle/`
    ),
    step1: withLocaleChannel(
      (locale, channel) => `/${channel.urlSlug}/${locale}/custom-map-puzzle/steps/1/`
    ),
    step2: withLocaleChannel(
      (locale, channel) => `/${channel.urlSlug}/${locale}/custom-map-puzzle/steps/2/`
    ),
  },
  products: {
    index: withLocaleChannel((locale, channel) => `/${channel.urlSlug}/${locale}/products/`),
    details: withLocaleChannelWithVars<{ slug: string }>(
      (locale, channel, { slug }) => `/${channel.urlSlug}/${locale}/products/${slug}/`
    ),
    categories: withLocaleChannelWithVars<{ slug?: string }>(
      (locale, channel, { slug }) =>
        `/${channel.urlSlug}/${locale}/products/categories/` + (slug && `${slug}/`)
    ),
    jigsawPuzzles: withLocaleChannelWithVars<{
      brand?: string;
      series?: string;
      theme?: string;
      artist?: string;
      pieceCount?: string;
    }>((locale, channel, { brand, series, theme, artist, pieceCount }) => {
      let url = `/${channel.urlSlug}/${locale}/products/categories/jigsaw-puzzles/`;

      // Build URL in canonical order: brand, series, theme, artist, pieces
      if (brand) {
        url += `brand-${brand}/`;
      }
      if (series) {
        url += `series-${series}/`;
      }
      if (theme) {
        url += `theme-${theme}/`;
      }
      if (artist) {
        url += `by-${artist}/`;
      }
      if (pieceCount) {
        url += `${pieceCount}/`;
      }

      return url;
    }),
    attributeWithCategory: withLocaleChannelWithVars<{
      categorySlug: string;
      attributeSlug: string;
      valueSlug: string;
    }>(
      (locale, channel, { categorySlug, attributeSlug, valueSlug }) =>
        `/${channel.urlSlug}/${locale}/products/${categorySlug}/by/${attributeSlug}/${valueSlug}/`
    ),
    collectionWithCategory: withLocaleChannelWithVars<{ slug: string; collectionSlug: string }>(
      (locale, channel, { slug, collectionSlug }) =>
        `/${channel.urlSlug}/${locale}/products/categories/${slug}/${collectionSlug}/`
    ),
    collections: withLocaleChannelWithVars<{ slug: string }>(
      (locale, channel, { slug }) => `/${channel.urlSlug}/${locale}/products/collections/${slug}/`
    ),
  },
  staticMap: () => '/api/staticmap/',
};

export {
  urls,
  imgUrl,
  parseParams,
  absoluteify,
  schLocaleChannel,
  withLocaleChannel,
  withLocaleChannelWithVars,
  canonicalize,
};
